import { IntlMessageFormat } from "intl-messageformat";
import {
  catalogs as defaultCatalogs,
  flattenCatalog,
  type Catalog,
  type MessageKey,
} from "./catalog";
import { FALLBACK_LOCALE, directionOf, intlLocaleOf, type Direction, type Locale } from "./locale";

export type MessageParam = string | number | boolean | Date | null | undefined;
export type MessageParams = Readonly<Record<string, MessageParam>>;

export type MissingMessageReason =
  /** Key absent (or empty) in the active locale; the English message was used. */
  | "fallback"
  /** Key absent in every catalog; the key itself was rendered. */
  | "missing"
  /** The ICU message could not be formatted (syntax error, missing param). */
  | "format-error";

export interface MissingMessageEvent {
  readonly key: string;
  readonly locale: Locale;
  readonly reason: MissingMessageReason;
  readonly error?: unknown;
}

export type MissingMessageHandler = (event: MissingMessageEvent) => void;

export interface TranslatorOptions {
  readonly locale: Locale;
  /** Override catalogs (tests, previews). Defaults to the bundled `en.json` / `ar.json`. */
  readonly catalogs?: Partial<Record<Locale, Catalog>>;
  /** Called for every missing/fallback/format problem. Defaults to a de-duplicated `console.warn`. */
  readonly onMissing?: MissingMessageHandler;
}

export interface Translator {
  readonly locale: Locale;
  readonly dir: Direction;
  /** Formats an ICU message. Never returns an empty string. */
  t(key: MessageKey, params?: MessageParams): string;
  /** Whether the key exists in the active locale (without fallback). */
  has(key: string): boolean;
}

const warned = new Set<string>();

/** Default missing-message logger: warns once per (locale, key, reason). */
export const warnMissingMessage: MissingMessageHandler = (event) => {
  const id = `${event.locale}\u0000${event.key}\u0000${event.reason}`;
  if (warned.has(id)) return;
  warned.add(id);
  const what =
    event.reason === "fallback"
      ? "missing, using English"
      : event.reason === "missing"
        ? "missing in every locale, rendering the key"
        : "could not be formatted";
  console.warn(`[i18n] "${event.key}" (${event.locale}) ${what}`, event.error ?? "");
};

const flatCache = new WeakMap<Catalog, Map<string, string>>();
function flat(catalog: Catalog | undefined): Map<string, string> {
  if (!catalog) return new Map();
  let map = flatCache.get(catalog);
  if (!map) {
    map = flattenCatalog(catalog);
    flatCache.set(catalog, map);
  }
  return map;
}

function lookup(map: Map<string, string>, key: string): string | undefined {
  const value = map.get(key);
  return value === undefined || value.trim() === "" ? undefined : value;
}

export function createTranslator(options: TranslatorOptions): Translator {
  const { locale } = options;
  const onMissing = options.onMissing ?? warnMissingMessage;
  const source = { ...defaultCatalogs, ...options.catalogs };
  const active = flat(source[locale]);
  const fallback = flat(source[FALLBACK_LOCALE]);
  const formatters = new Map<string, IntlMessageFormat>();

  function format(key: string, pattern: string, patternLocale: Locale, params?: MessageParams) {
    // Plain messages (the vast majority) skip ICU parsing entirely.
    if (!pattern.includes("{") && !pattern.includes("'")) return pattern;
    const cacheKey = `${patternLocale}\u0000${key}`;
    let formatter = formatters.get(cacheKey);
    if (!formatter) {
      formatter = new IntlMessageFormat(pattern, intlLocaleOf(patternLocale));
      formatters.set(cacheKey, formatter);
    }
    const result = formatter.format<string>(params as Record<string, MessageParam>);
    return Array.isArray(result) ? result.join("") : String(result);
  }

  function t(key: MessageKey, params?: MessageParams): string {
    let pattern = lookup(active, key);
    let patternLocale = locale;
    if (pattern === undefined) {
      pattern = lookup(fallback, key);
      patternLocale = FALLBACK_LOCALE;
      if (pattern === undefined) {
        onMissing({ key, locale, reason: "missing" });
        return key;
      }
      if (locale !== FALLBACK_LOCALE) onMissing({ key, locale, reason: "fallback" });
    }
    try {
      const text = format(key, pattern, patternLocale, params);
      return text.trim() === "" ? key : text;
    } catch (error) {
      onMissing({ key, locale, reason: "format-error", error });
      return pattern;
    }
  }

  return {
    locale,
    dir: directionOf(locale),
    t,
    has: (key) => lookup(active, key) !== undefined,
  };
}

const defaultTranslators = new Map<Locale, Translator>();

/** One-shot translation with the bundled catalogs (non-React code, e.g. emails, tests). */
export function translate(locale: Locale, key: MessageKey, params?: MessageParams): string {
  let translator = defaultTranslators.get(locale);
  if (!translator) {
    translator = createTranslator({ locale });
    defaultTranslators.set(locale, translator);
  }
  return translator.t(key, params);
}
